import React, { useState, useEffect } from "react";
import { astryx, newAxiosClient } from "@shadowcue/ui";
import type { IUser } from "../../types/iuser";
import LlmConfigurationFields, {
  type ILlmConfigurationForm,
} from "../admin/llm_configuration/form";

type SettingsTab = "general" | "llm";

interface SettingsModalProps {
  isOpen: boolean;
  currentUser: IUser;
  onClose: () => void;
  /** Tab to show when the dialog opens. Defaults to General. */
  initialTab?: SettingsTab;
}

const axios = newAxiosClient();

const blankLlmConfiguration: ILlmConfigurationForm = {
  engine: "claude",
  apiKey: "",
};

const SettingsModal = ({
  isOpen,
  currentUser,
  onClose,
  initialTab = "general",
}: SettingsModalProps) => {
  const [activeTab, setActiveTab] = useState<SettingsTab>(initialTab);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // General tab — appearance theme toggle. Keeps writing `data-theme` on
  // <html> and localStorage — that attribute is the source of truth
  // useDocumentTheme observes and the inline script in
  // application.html.erb replays on load, so breaking this contract breaks
  // theming app-wide.
  const [isLightMode, setIsLightMode] = useState<boolean>(
    () => document.documentElement.getAttribute("data-theme") === "light",
  );

  const toggleTheme = (checked: boolean) => {
    setIsLightMode(checked);
    const next = checked ? "light" : "dark";
    document.documentElement.setAttribute("data-theme", next);
    localStorage.setItem("theme", next);
  };

  // LLM Configuration tab
  const [llmValues, setLlmValues] =
    useState<ILlmConfigurationForm>(blankLlmConfiguration);
  const [llmLoading, setLlmLoading] = useState(true);
  // Whether the user has edited the LLM fields since they were loaded. Save
  // only PATCHes when this is true: an org with no saved configuration gets
  // `api_key: null` back from the edit endpoint, and sending that null back
  // untouched fails the model's "must be set" validation with a 422.
  const [llmDirty, setLlmDirty] = useState(false);

  const fetchLlmConfiguration = async () => {
    setLlmLoading(true);
    setLlmDirty(false);
    try {
      const res = await axios.get("/api/organization/llm_configuration/edit", {
        headers: { AUTHORIZATION: `Bearer ${currentUser.authToken}` },
      });
      const config = res.data.llmConfiguration;
      setLlmValues({
        id: config.id,
        engine: config.engine,
        apiKey: config.apiKey,
      });
    } catch (err) {
      console.log(err);
      setError("Failed to load LLM configuration");
    } finally {
      setLlmLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
      fetchLlmConfiguration();
    }
  }, [isOpen, initialTab]);

  const handleLlmChange = (values: ILlmConfigurationForm) => {
    setLlmValues(values);
    setLlmDirty(true);
  };

  const saveLlmConfiguration = async () => {
    await axios.patch(
      "/api/organization/llm_configuration",
      {
        llmConfiguration: {
          engine: llmValues.engine,
          apiKey: llmValues.apiKey,
        },
      },
      {
        headers: { AUTHORIZATION: `Bearer ${currentUser.authToken}` },
        withCredentials: true,
      },
    );
  };

  // The General tab has nothing to persist server-side: Email is read-only and
  // the appearance toggle writes `data-theme` + localStorage the moment it
  // changes. It previously PATCHed /api/users/:id with `openaiKey: ""`, which
  // was not just pointless but destructive — that controller runs
  // `sync_admin_role` on every update, and because the request carries no
  // `admin` param, `should_be_admin?` resolves false and the user's admin role
  // is deleted. Saving settings silently demoted the current user. LLM
  // configuration is the only thing here that needs a save, and only when the
  // user actually edited it; otherwise Save just closes the modal.
  const handleSave = async () => {
    setSubmitting(true);
    setError(null);
    try {
      if (llmDirty) {
        await saveLlmConfiguration();
      }
      onClose();
    } catch (err) {
      console.log(err);
      setError("Failed to save settings");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (nextIsOpen: boolean) => {
    if (!nextIsOpen && !submitting) {
      onClose();
    }
  };

  return (
    <astryx.Dialog isOpen={isOpen} onOpenChange={handleOpenChange} purpose="form" width={480}>
      <astryx.Layout
        header={<astryx.DialogHeader title="Settings" onOpenChange={handleOpenChange} />}
        content={
          <astryx.LayoutContent>
            <astryx.VStack gap={4}>
              <astryx.TabList
                value={activeTab}
                onChange={(value: string) => setActiveTab(value as SettingsTab)}
              >
                <astryx.Tab value="general" label="General" />
                <astryx.Tab value="llm" label="LLM Configuration" />
              </astryx.TabList>

              {/* Detached: this floats on its own below the tabs, so it must
                  not use the default attached variant (square top corners,
                  meant to tuck under an input). */}
              {error && (
                <astryx.FieldStatus type="error" message={error} variant="detached" />
              )}

              {activeTab === "general" && (
                <astryx.VStack gap={4}>
                  <astryx.TextInput label="Email" value={currentUser.email} isDisabled />
                  <astryx.Switch
                    label="Light mode"
                    value={isLightMode}
                    onChange={toggleTheme}
                  />
                </astryx.VStack>
              )}

              {activeTab === "llm" && (
                <LlmConfigurationFields
                  values={llmValues}
                  onChange={handleLlmChange}
                  loading={llmLoading}
                />
              )}
            </astryx.VStack>
          </astryx.LayoutContent>
        }
        footer={
          <astryx.LayoutFooter hasDivider>
            <astryx.HStack gap={2} justify="end">
              <astryx.Button label="Cancel" onClick={onClose} isDisabled={submitting} />
              <astryx.Button
                label="Save"
                variant="primary"
                onClick={handleSave}
                isLoading={submitting}
                isDisabled={llmLoading}
              />
            </astryx.HStack>
          </astryx.LayoutFooter>
        }
      />
    </astryx.Dialog>
  );
};

export type { SettingsTab };
export default SettingsModal;
